/**
 * B481 fix round 1 - what the Settings first screen drew last time on this device, so its loading
 * frame can hold the same height from the first paint (the Today layout memory,
 * `lib/todayLayoutMemory.ts`, is the precedent).
 *
 * WHY. The first frame cannot know which of the plan card's loaded states it is waiting for (a free
 * card is 292 px, a paid one 169, a guardian-covered one 113, a beta one 398), and a frame that holds
 * one guess moves the rows below it for every account the guess is wrong for. The account's own last
 * visit is the best fact the frame has before the plan read answers.
 *
 * WHAT. Two small facts, nothing else, and both are names or booleans:
 *   - `shape`: which loaded state the plan card drew (`PlanShape`, a fixed list of words), or null when
 *     the state is not one of them. No price, no date, no name, no id, no payment fact beyond the word.
 *   - `guardians`: whether "Guardians and access" was drawn (the server's `relationships_v1` answer), so
 *     the late row's place can be held.
 * The one identifier is the user id the record belongs to.
 *
 * WHERE. One localStorage slot (`tayro.settingsPlan.v1`), stamped with the user id. The first screen
 * reads it only when the signed-in user's id matches the stamp; a record for anyone else, or no
 * signed-in user, reads as no memory and the frame holds the free card, as it did before this module.
 * A second account that signs in replaces the first one's record on its first write.
 *
 * GONE ON SIGN-OUT AND ON DELETION. `contexts/AuthContext` (the SIGNED_OUT branch) and `lib/deviceState`
 * (`clearOnAccountDeletion`) both call `clearPlanShape`, so a shared phone never draws another person's
 * plan state, and a deleted account leaves nothing behind.
 *
 * FAIL-SOFT. Every read and write is in a try and catch (private modes and blocked storage throw), a
 * record that is not exactly the shape written here reads as no memory, and the page works the same
 * without it: the frame is only plainer.
 */

import type { UserTierData } from '../hooks/useUserTier';

export const PLAN_SHAPE_KEY = 'tayro.settingsPlan.v1';

/**
 * The loaded states the plan card can draw, by the geometry they give it:
 *   free          the Free plan card, with its upgrade block
 *   membership    a paid Stripe membership (renewing, or active with no period end: the same card)
 *   covered       a guardian pays (a license, or the archived Family cover)
 *   coveredMinor  the same, for a known minor (the legacy ladder reads it differently)
 *   beta          beta access
 *   pastDue       payment past due
 *   minor         a known minor nobody covers yet (the guardian ladder)
 */
export type PlanShape = 'free' | 'membership' | 'covered' | 'coveredMinor' | 'beta' | 'pastDue' | 'minor';

const SHAPES: readonly PlanShape[] = ['free', 'membership', 'covered', 'coveredMinor', 'beta', 'pastDue', 'minor'];
const MAX_USER_ID = 128;

/**
 * Which shape a tier payload draws, or null for a state this memory does not name (an Apple membership
 * seen from the web, a team tier). The order is the order the plan card and the section decide in:
 * `PlanCard.tsx` `coveredPlan` and `webPlanState`, and `SubscriptionSection`'s own branches. A test
 * runs the real functions against this one on a battery of payloads and fails if they ever differ.
 */
export function planShapeOf(data: UserTierData): PlanShape | null {
  const covered =
    !!data.coverage ||
    (data.tier === 'family' && data.subscriptionStatus !== 'active' && data.subscriptionStatus !== 'past_due');
  if (covered) return data.derivedMinor === true ? 'coveredMinor' : 'covered';
  if (data.derivedMinor === true) return 'minor';
  if (data.isBeta) return 'beta';
  if (data.subscriptionStatus === 'past_due') return 'pastDue';
  if (data.tier === 'athlete' && data.subscriptionStatus === 'active' && data.subscriptionSource !== 'storekit') {
    return 'membership';
  }
  if (data.tier === 'free') return 'free';
  return null;
}

/**
 * A tier payload with the facts that decide a shape's geometry and nothing else, for the invisible
 * placeholder the loading frame draws. Its words are what the shape's real card prints with typical
 * values (a four letter guardian name, a long month date): only its height is ever used.
 */
export function ghostTierData(shape: PlanShape): UserTierData {
  const base: UserTierData = {
    tier: 'free',
    features: {},
    isBeta: false,
    betaExpiresAt: null,
    subscriptionStatus: null,
    subscriptionPeriodEnd: null,
    derivedMinor: false,
    derivedUnder18: false,
    subscriptionSource: null,
    familyAskSentAt: null,
  };
  const coverage = { source: 'guardian' as const, guardianFirstName: 'Alex', renewsAt: null, endsAt: null };
  switch (shape) {
    case 'free':
      return base;
    case 'membership':
      return {
        ...base,
        tier: 'athlete',
        subscriptionStatus: 'active',
        subscriptionPeriodEnd: '2026-09-30T12:00:00.000Z',
        interval: 'year',
      };
    case 'covered':
      return { ...base, tier: 'athlete', coverage };
    case 'coveredMinor':
      return { ...base, tier: 'athlete', coverage, derivedMinor: true, derivedUnder18: true };
    case 'beta':
      return { ...base, tier: 'athlete', isBeta: true, betaExpiresAt: '2026-12-01T12:00:00.000Z' };
    case 'pastDue':
      return {
        ...base,
        tier: 'athlete',
        subscriptionStatus: 'past_due',
        subscriptionPeriodEnd: '2026-09-30T12:00:00.000Z',
        interval: 'month',
      };
    case 'minor':
      return { ...base, derivedMinor: true, derivedUnder18: true };
  }
}

interface StoredRecord {
  v: 1;
  /** The user this memory is for. */
  user: string;
  shape: PlanShape | null;
  guardians: boolean | null;
}

function parseRecord(raw: string | null): StoredRecord | null {
  if (!raw) return null;
  try {
    const x: unknown = JSON.parse(raw);
    if (typeof x !== 'object' || x === null) return null;
    const r = x as Record<string, unknown>;
    if (r.v !== 1) return null;
    if (typeof r.user !== 'string' || r.user === '' || r.user.length > MAX_USER_ID) return null;
    const shape = r.shape;
    if (shape !== null && !SHAPES.includes(shape as PlanShape)) return null;
    const guardians = r.guardians;
    if (guardians !== null && typeof guardians !== 'boolean') return null;
    return { v: 1, user: r.user, shape: shape as PlanShape | null, guardians };
  } catch {
    return null;
  }
}

function readRecord(): StoredRecord | null {
  try {
    return parseRecord(window.localStorage.getItem(PLAN_SHAPE_KEY));
  } catch {
    return null;
  }
}

/** Write one field for `userId`, keeping the other if the slot is already this user's, else starting fresh. */
function write(userId: string, patch: Partial<Pick<StoredRecord, 'shape' | 'guardians'>>): void {
  if (!userId || userId.length > MAX_USER_ID) return;
  try {
    const prev = readRecord();
    const own = prev && prev.user === userId ? prev : null;
    const record: StoredRecord = {
      v: 1,
      user: userId,
      shape: own?.shape ?? null,
      guardians: own?.guardians ?? null,
      ...patch,
    };
    const raw = JSON.stringify(record);
    if (window.localStorage.getItem(PLAN_SHAPE_KEY) === raw) return;
    window.localStorage.setItem(PLAN_SHAPE_KEY, raw);
  } catch {
    /* best-effort: the frame is plainer without a memory, the page is the same */
  }
}

/** Remember the shape the loaded plan card drew, for this user. A null shape forgets the last one. */
export function rememberPlanShape(userId: string | null, shape: PlanShape | null): void {
  if (userId) write(userId, { shape });
}

/** The shape this user's last visit drew, or null: no memory, another user's record, or no signed-in user. */
export function recallPlanShape(userId: string | null): PlanShape | null {
  if (!userId) return null;
  const record = readRecord();
  return record && record.user === userId ? record.shape : null;
}

/** Remember whether "Guardians and access" was drawn, for this user (the server said `relationships_v1` on or off). */
export function rememberGuardiansRow(userId: string | null, drawn: boolean): void {
  if (userId) write(userId, { guardians: drawn });
}

/** True only when this user's last visit drew the row. No memory, another user's record, no user: false. */
export function recallGuardiansRow(userId: string | null): boolean {
  if (!userId) return false;
  const record = readRecord();
  return !!record && record.user === userId && record.guardians === true;
}

/**
 * Forget it: sign-out (`contexts/AuthContext`, the SIGNED_OUT branch) and account deletion
 * (`lib/deviceState`'s `clearOnAccountDeletion`) both call it. Never throws.
 */
export function clearPlanShape(): void {
  try {
    window.localStorage.removeItem(PLAN_SHAPE_KEY);
  } catch {
    /* best-effort: blocked storage holds no memory to leave behind */
  }
}
